import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import type { PageGuideDefinition } from "../types";

/** Target ids for Clock — use with `guideTargetProps()`. */
export const CLOCKING_GUIDE_TARGETS = {
  dateRange: "clocking-date-range",
  exceptionsOnly: "clocking-exceptions-only",
  legend: "clocking-legend",
  grid: "clocking-grid",
  /** One date-column header — used to point at clear-by-day. */
  dateHeader: "clocking-date-header",
  emptyState: "clocking-empty-state",
  inbox: "clocking-inbox",
  settings: "clocking-settings",
} as const;

export const clockingGuide: PageGuideDefinition = {
  id: "clocking-v2",
  viewType: VIEW_TYPES.clocking,
  steps: [
    {
      target: CLOCKING_GUIDE_TARGETS.dateRange,
      placement: { side: "bottom", align: "start" },
      title: "Pick a review window",
      body: "Choose the dates to load. Clock starts on the last 7 days so you can clear recent exceptions quickly.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.exceptionsOnly,
      placement: { side: "bottom", align: "start" },
      title: "Focus on problems",
      body: "Exceptions only hides clean punches so you review late, missed, or time-off items first. Turn it off to see every day.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.legend,
      placement: { side: "bottom", align: "start" },
      title: "Status colors",
      body: "Hover Legend to see what each cell color means while you scan the grid.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.grid,
      placement: { side: "top", align: "start" },
      title: "Attendance matrix",
      body: "Workers are rows and dates are columns. Click a cell for one person’s day. Days with open work show a count under the date (for example “3 open”).",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.dateHeader,
      placement: { side: "bottom", align: "center" },
      title: "Clear a whole day",
      body: "Click this date header to open the clear queue for that column only. Hover shows Clear when the day still has open exceptions.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.inbox,
      placement: { side: "bottom", align: "end" },
      title: "Clear queue",
      body: "Use Clear queue in the toolbar to clear open exceptions across every day in the range. Prefer a date header when you only want one column.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.settings,
      placement: { side: "bottom", align: "end" },
      title: "Board clocking settings",
      body: "Managers can enable clocking, set the shift window, geofence, and incentives for this board.",
    },
  ],
};

export const clockingEmptyGuide: PageGuideDefinition = {
  id: "clocking-v1-empty",
  viewType: VIEW_TYPES.clocking,
  steps: [
    {
      target: CLOCKING_GUIDE_TARGETS.dateRange,
      placement: { side: "bottom", align: "start" },
      title: "Pick a review window",
      body: "Widen or shift the date range if nothing is showing for these days.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.exceptionsOnly,
      placement: { side: "bottom", align: "start" },
      title: "Exceptions filter",
      body: "With Exceptions only on, complete punches stay hidden. Turn it off to include every record in range.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.emptyState,
      placement: { side: "top", align: "center" },
      title: "Nothing to review",
      body: "Use Show all punches or Last 14 days when the filter or range is too narrow.",
    },
    {
      target: CLOCKING_GUIDE_TARGETS.settings,
      placement: { side: "bottom", align: "end" },
      title: "Board clocking settings",
      body: "Managers can enable clocking and tune schedule, geofence, and incentives here.",
    },
  ],
};
